import {
  Box,
  Checkbox,
  Collapse,
  Flex,
  HStack,
  Icon,
  IconButton,
  Link,
  Stack,
  Tag,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";
import { FiChevronDown, FiChevronUp, FiExternalLink, FiLink2 } from "react-icons/fi";
import { Card } from "../../ui/Card";
import { Money, formatMoneyCents } from "../../ui/Money";
import { StatusPill } from "../../ui/StatusPill";
import { BillingItem } from "./types";
import { ITEM_TYPE_LABEL, formatDate } from "./format";
import { LinesBreakdown } from "./LinesBreakdown";
import { billingStatePill } from "./states";

type Props = {
  items: BillingItem[];
  isLoading: boolean;
  // A failed read is said, never shown as an empty account (audit C37).
  isError?: boolean;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: (selectAll: boolean) => void;
  renderRowExtras?: (item: BillingItem) => React.ReactNode;
};

const Row = ({
  item,
  selected,
  onToggle,
  selectable,
  renderExtras,
}: {
  item: BillingItem;
  selected: boolean;
  onToggle: () => void;
  selectable: boolean;
  renderExtras?: (item: BillingItem) => React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const pill = billingStatePill(item.state);

  const insuranceCoveredHint =
    item.amounts.coveredByInsuranceCents > 0
      ? `${formatMoneyCents(item.amounts.coveredByInsuranceCents)} covered by insurance`
      : null;
  const insurancePendingHint =
    item.amounts.insurerOutstandingCents > 0
      ? `${formatMoneyCents(item.amounts.insurerOutstandingCents)} awaiting insurance response`
      : null;
  const membershipHint =
    item.amounts.coveredByMembershipCents > 0
      ? `${formatMoneyCents(item.amounts.coveredByMembershipCents)} covered by membership`
      : null;
  // A financed purchase is paid off through its installments, not from this
  // screen — the balance shown here is what is left of the whole plan, and
  // the plan's next installment is said beside it (audit C37).
  const paymentPlanHint = item.paymentPlan
    ? item.paymentPlan.nextChargeAt && item.paymentPlan.nextInstallmentCents > 0
      ? `On your payment plan — next ${formatMoneyCents(item.paymentPlan.nextInstallmentCents)} on ${formatDate(item.paymentPlan.nextChargeAt)}`
      : "Paid through your payment plan"
    : null;

  return (
    <Box borderBottomWidth="1px" borderColor="#f0f0f0" _last={{ borderBottomWidth: 0 }}>
      <Flex
        align="center"
        px={{ base: 3, md: 4 }}
        py={3}
        gap={3}
        cursor={selectable ? "default" : "pointer"}
        onClick={(e) => {
          if ((e.target as HTMLElement).tagName === "INPUT") return;
          setOpen((v) => !v);
        }}
        _hover={{ bg: "#fafafa" }}
      >
        {selectable ? (
          <Checkbox
            isChecked={selected}
            onChange={onToggle}
            onClick={(e) => e.stopPropagation()}
            colorScheme="blue"
          />
        ) : (
          <Box w={4} />
        )}
        <Box flex={1} minW={0}>
          <HStack spacing={2} flexWrap="wrap">
            <Text
              fontSize="14px"
              fontWeight="500"
              color="#1a1f36"
              noOfLines={1}
            >
              {item.title}
            </Text>
            {item.pendingPaymentLink?.url && (
              // Clickable tag — acts as both visual indicator AND the action,
              // so we don't shift the right-side columns on rows that have a
              // payment link.
              <Tag
                as={Link}
                href={item.pendingPaymentLink.url}
                isExternal
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                size="sm"
                bg="#eff6ff"
                color="#1d4ed8"
                borderRadius="full"
                px={2}
                py={0}
                fontSize="11px"
                cursor="pointer"
                _hover={{ bg: "#dbeafe", textDecoration: "none" }}
              >
                <Icon as={FiLink2} mr={1} fontSize="12px" />
                Payment link
                <Icon as={FiExternalLink} ml={1} fontSize="11px" />
              </Tag>
            )}
          </HStack>
          <HStack spacing={2} fontSize="12px" color="#6b7280" mt={0.5}>
            <Text>{ITEM_TYPE_LABEL[item.type] || item.type}</Text>
            {item.subtitle && <Text>· {item.subtitle}</Text>}
            {item.occurredAt > 0 && <Text>· {formatDate(item.occurredAt)}</Text>}
          </HStack>
        </Box>
        <Box display={{ base: "none", md: "block" }}>
          <StatusPill variant={pill.variant}>{pill.label}</StatusPill>
        </Box>
        <Box minW="90px" textAlign="right">
          <Text fontSize="14px" fontWeight="600" color="#1a1f36">
            <Money cents={item.amounts.outstandingCents > 0 ? item.amounts.outstandingCents : item.amounts.totalCents} />
          </Text>
          {item.amounts.outstandingCents > 0 && item.amounts.totalCents !== item.amounts.outstandingCents && (
            <Text fontSize="11px" color="#6b7280">
              of {formatMoneyCents(item.amounts.totalCents)}
            </Text>
          )}
        </Box>
        <IconButton
          aria-label="Expand"
          variant="ghost"
          size="xs"
          icon={<Icon as={open ? FiChevronUp : FiChevronDown} />}
          color="#6b7280"
        />
      </Flex>
      <Collapse in={open}>
        <Box px={{ base: 3, md: 4 }} pb={4} pl={{ base: 10, md: 12 }}>
          <Stack spacing={1.5} fontSize="13px" color="#475467">
            <Text>Total: {formatMoneyCents(item.amounts.totalCents)}</Text>
            {item.amounts.paidCents > 0 && (
              <Text>Paid so far: {formatMoneyCents(item.amounts.paidCents)}</Text>
            )}
            {insuranceCoveredHint && <Text>{insuranceCoveredHint}</Text>}
            {insurancePendingHint && <Text>{insurancePendingHint}</Text>}
            {membershipHint && <Text>{membershipHint}</Text>}
            {paymentPlanHint && (
              <Text color="#8a6d1f">{paymentPlanHint}</Text>
            )}
            <LinesBreakdown lines={item.lines || []} />
            {renderExtras && renderExtras(item)}
            <Box display={{ base: "block", md: "none" }} mt={1}>
              <StatusPill variant={pill.variant}>{pill.label}</StatusPill>
            </Box>
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
};

export const ItemsTable = ({
  items,
  isLoading,
  isError = false,
  selectedIds,
  onToggle,
  onToggleAll,
  renderRowExtras,
}: Props) => {
  // What can be paid from here — never a debt a payment plan collects.
  const payable = items.filter((i) => i.amounts.payableNowCents > 0);
  const allPayableSelected =
    payable.length > 0 && payable.every((i) => selectedIds.has(i.itemId));

  return (
    <Card overflow="hidden">
      <Flex
        align="center"
        px={{ base: 3, md: 4 }}
        py={2.5}
        borderBottomWidth="1px"
        borderColor="#f0f0f0"
        bg="#fafafa"
        gap={3}
      >
        <Checkbox
          isChecked={allPayableSelected}
          isIndeterminate={!allPayableSelected && selectedIds.size > 0}
          onChange={(e) => onToggleAll(e.target.checked)}
          isDisabled={payable.length === 0}
          colorScheme="blue"
        />
        <Text fontSize="12px" color="#6b7280" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600">
          Item
        </Text>
        <Box flex={1} />
        <Text
          display={{ base: "none", md: "block" }}
          fontSize="12px"
          color="#6b7280"
          textTransform="uppercase"
          letterSpacing="0.04em"
          fontWeight="600"
        >
          Status
        </Text>
        <Text
          minW="90px"
          textAlign="right"
          fontSize="12px"
          color="#6b7280"
          textTransform="uppercase"
          letterSpacing="0.04em"
          fontWeight="600"
        >
          Amount
        </Text>
        <Box w={6} />
      </Flex>
      {isLoading && (
        <Box px={4} py={10} textAlign="center" color="#6b7280" fontSize="14px">
          Loading…
        </Box>
      )}
      {!isLoading && isError && (
        <Box px={4} py={10} textAlign="center" color="#b42318" fontSize="14px">
          We couldn't load your bills. Try again in a moment.
        </Box>
      )}
      {!isLoading && !isError && items.length === 0 && (
        <Box px={4} py={10} textAlign="center" color="#6b7280" fontSize="14px">
          Nothing to show with the current filters.
        </Box>
      )}
      {!isLoading && items.length > 0 && (
        <VStack spacing={0} align="stretch">
          {items.map((item) => (
            <Row
              key={item.itemId}
              item={item}
              selected={selectedIds.has(item.itemId)}
              onToggle={() => onToggle(item.itemId)}
              selectable={item.amounts.payableNowCents > 0}
              renderExtras={renderRowExtras}
            />
          ))}
        </VStack>
      )}
    </Card>
  );
};
